import React, { useState } from "react";
import { createUserWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { auth, db, googleProvider } from "./firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  const validateForm = () => {
    if (password.length < 6) {
      setMessage("❌ Password must be at least 6 characters long.");
      return false;
    }

    if (password !== confirmPassword) {
      setMessage("❌ Passwords do not match.");
      return false;
    }

    if (name.trim().length < 2) {
      setMessage("❌ Please enter a valid name (at least 2 characters).");
      return false;
    }

    return true;
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      // ✅ Save user info in Firestore
      await setDoc(doc(db, "users", uid), {
        name: name.trim(),
        email,
        role: "user",
        createdAt: new Date().toISOString(),
        loginMethod: "email",
        profileComplete: false,
        referralCompleted: false // New field to track referral completion
      });

      setMessage("✅ Account created successfully! Redirecting...");

      // Redirect to referral page first
      setTimeout(() => {
        navigate("/how-did-you-find-us");
      }, 1500);

    } catch (error) {
      console.error("Signup error:", error);
      let errorMessage = "❌ Signup failed. Please try again.";

      if (error.code === "auth/email-already-in-use") {
        errorMessage = "❌ An account with this email already exists.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "❌ Invalid email address.";
      } else if (error.code === "auth/weak-password") {
        errorMessage = "❌ Password is too weak. Please choose a stronger password.";
      }

      setMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    setMessage("");

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // ✅ Check if user already exists
      const userDoc = await getDoc(doc(db, "users", user.uid));

      if (userDoc.exists()) {
        // User already exists - check if they completed referral step
        const userData = userDoc.data();
        const role = userData.role;
        const referralCompleted = userData.referralCompleted;

        if (!referralCompleted) {
          setMessage("✅ Welcome back! Please complete your setup...");
          setTimeout(() => {
            navigate("/how-did-you-find-us");
          }, 1500);
        } else {
          setMessage("✅ Welcome back! Redirecting...");
          setTimeout(() => {
            if (role === "admin") {
              navigate("/admin/upload");
            } else {
              navigate("/dashboard");
            }
          }, 1500);
        }
      } else {
        // ✅ New user - create user record
        await setDoc(doc(db, "users", user.uid), {
          name: user.displayName || "User",
          email: user.email,
          role: "user",
          createdAt: new Date().toISOString(),
          loginMethod: "google",
          profileComplete: false,
          referralCompleted: false // New field for tracking referral completion
        });

        setMessage("✅ Account created successfully! Redirecting...");

        // Redirect to referral page first
        setTimeout(() => {
          navigate("/how-did-you-find-us");
        }, 1500);
      }
    } catch (error) {
      console.error("Google Sign-Up Error:", error);
      let errorMessage = "❌ Google sign-up failed. Please try again.";

      if (error.code === "auth/popup-closed-by-user") {
        errorMessage = "❌ Sign-up cancelled.";
      } else if (error.code === "auth/popup-blocked") {
        errorMessage = "❌ Popup blocked. Please allow popups and try again.";
      } else if (error.code === "auth/account-exists-with-different-credential") {
        errorMessage = "❌ An account already exists with this email using a different sign-in method.";
      }

      setMessage(errorMessage);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  return (
    <div
      className="min-h-screen bg-white flex items-center justify-center p-6"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}
    >
      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Back Button */}
        <button
          onClick={handleBackToHome}
          className="mb-10 flex items-center text-[13px] font-medium text-gray-600 hover:text-black transition-colors duration-200 group"
        >
          <span className="mr-2 transition-transform duration-200 group-hover:-translate-x-0.5">←</span>
          Back to Home
        </button>

        {/* Signup Card */}
        <div className="bg-white border border-black/[0.08] rounded-2xl shadow-sm p-8">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-6">
              <div className="w-14 h-14 bg-black rounded-2xl flex items-center justify-center">
                <span className="text-white text-xl">📝</span>
              </div>
            </div>
            <h1 className="text-[28px] font-semibold text-black tracking-[-0.03em] leading-tight mb-2">
              Create Account
            </h1>
            <p className="text-[15px] text-gray-500">
              Join Residency Companion to find your perfect medical college
            </p>
          </div>

          {/* Google Sign-Up Button */}
          <button
            onClick={handleGoogleSignUp}
            disabled={isGoogleLoading}
            className="w-full mb-6 bg-white hover:bg-gray-50 border border-black/[0.15] hover:border-black/30 text-black py-3.5 px-4 rounded-full text-[15px] font-medium transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white flex items-center justify-center"
          >
            {isGoogleLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-black/20 border-t-black mr-2" />
                Creating account...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>

          {/* Divider */}
          <div className="mb-6 flex items-center">
            <div className="flex-1 border-t border-black/[0.06]" />
            <span className="px-4 text-[13px] text-gray-400">or</span>
            <div className="flex-1 border-t border-black/[0.06]" />
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSignup} className="space-y-5">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-[13px] font-medium text-black mb-1.5">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white border border-black/[0.1] rounded-xl focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200 text-black placeholder-gray-400 text-[15px]"
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-[13px] font-medium text-black mb-1.5">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white border border-black/[0.1] rounded-xl focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200 text-black placeholder-gray-400 text-[15px]"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-[13px] font-medium text-black mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password (min. 6 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white border border-black/[0.1] rounded-xl focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200 text-black placeholder-gray-400 text-[15px] pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
                >
                  {showPassword ? "👁️" : "🙈"}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-[13px] font-medium text-black mb-1.5">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white border border-black/[0.1] rounded-xl focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200 text-black placeholder-gray-400 text-[15px] pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
                >
                  {showConfirmPassword ? "👁️" : "🙈"}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white py-3.5 px-4 rounded-full text-[15px] font-medium transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white mr-2" />
                  Creating Account...
                </>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          {/* Message */}
          {message && (
            <div
              className={`mt-6 p-4 rounded-xl text-[14px] font-medium ${
                message.includes("✅")
                  ? "bg-black/[0.04] text-gray-800 border border-black/[0.08]"
                  : "bg-red-50/90 text-red-900 border border-red-200/80"
              }`}
            >
              {message}
            </div>
          )}

          {/* Divider */}
          <div className="my-8 flex items-center">
            <div className="flex-1 border-t border-black/[0.06]" />
            <span className="px-4 text-[13px] text-gray-400">or</span>
            <div className="flex-1 border-t border-black/[0.06]" />
          </div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-[15px] text-gray-500 mb-4">Already have an account?</p>
            <button
              onClick={() => navigate("/login")}
              type="button"
              className="w-full border border-black/[0.15] hover:border-black/30 text-black hover:bg-gray-50 py-3.5 px-4 rounded-full text-[15px] font-medium transition-all duration-200"
            >
              Sign In
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-[12px] text-gray-400">
            By creating an account, you agree to our{" "}
            <span className="text-gray-500 cursor-not-allowed opacity-70">Terms of Service</span>
            {" "}and{" "}
            <span className="text-gray-500 cursor-not-allowed opacity-70">Privacy Policy</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Signup;
